/**
 * Conversation Settings Modal Component
 * 
 * Modal for managing conversation settings: mute, archive, and pin.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Switch, Typography, Divider, Button, Slider, Alert } from 'antd';
import { BellOutlined, InboxOutlined, PushpinOutlined, NotificationOutlined, SoundOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import type { Conversation } from '@municipality/types';
import { useBrowserNotifications } from '@municipality/chat';

const { Text } = Typography;

interface ConversationSettingsModalProps {
  open: boolean;
  conversation: Conversation | null;
  currentUserId: string;
  onClose: () => void;
  onMute: (conversationId: string, isMuted: boolean) => Promise<void>;
  onArchive: (conversationId: string, isArchived: boolean) => Promise<void>;
  onPin: (conversationId: string, isPinned: boolean) => Promise<void>;
}

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`;

const SettingInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: 16px;
`;

const SettingTitle = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const SettingDescription = styled(Text)`
  font-size: 12px;
  color: #999;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const ConversationSettingsModal: React.FC<ConversationSettingsModalProps> = ({
  open,
  conversation,
  currentUserId,
  onClose,
  onMute,
  onArchive,
  onPin,
}) => {
  const { t } = useTranslation('chat');
  const { message } = App.useApp();
  const {
    permission,
    settings: notificationSettings,
    supportsNotifications,
    requestPermission,
    updateSettings: updateNotificationSettings,
  } = useBrowserNotifications(currentUserId);

  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);

  // Check permission status when modal opens
  useEffect(() => {
    if (open && notificationSettings.enabled && permission === 'default') {
      setShowPermissionPrompt(true);
    } else {
      setShowPermissionPrompt(false);
    }
  }, [open, notificationSettings.enabled, permission]);

  if (!conversation) return null;

  const isMuted = conversation.isMuted?.[currentUserId] || false;
  const isArchived = conversation.isArchived?.[currentUserId] || false;
  const isPinned = conversation.pinnedBy?.includes(currentUserId) || false;

  const handleRequestPermission = async () => {
    const result = await requestPermission();
    if (result === 'granted') {
      message.success(t('notifications.permissionGranted') || 'Notification permission granted');
      setShowPermissionPrompt(false);
    } else if (result === 'denied') {
      message.warning(t('notifications.permissionDenied') || 'Notification permission denied');
    }
  };

  const handleMute = async (checked: boolean) => {
    try {
      await onMute(conversation.id, checked);
      message.success(
        checked
          ? t('settings.mutedSuccess') || 'Conversation muted'
          : t('settings.unmutedSuccess') || 'Conversation unmuted'
      );
    } catch {
      message.error(t('settings.muteError') || 'Failed to update mute setting');
    }
  };

  const handleArchive = async (checked: boolean) => {
    try {
      await onArchive(conversation.id, checked);
      message.success(
        checked
          ? t('settings.archivedSuccess') || 'Conversation archived'
          : t('settings.unarchivedSuccess') || 'Conversation unarchived'
      );
      onClose();
    } catch {
      message.error(t('settings.archiveError') || 'Failed to update archive setting');
    }
  };

  const handlePin = async (checked: boolean) => {
    try {
      await onPin(conversation.id, checked);
      message.success(
        checked
          ? t('settings.pinnedSuccess') || 'Conversation pinned'
          : t('settings.unpinnedSuccess') || 'Conversation unpinned'
      );
    } catch {
      message.error(t('settings.pinError') || 'Failed to update pin setting');
    }
  };

  return (
    <Modal
      title={t('settings.title') || 'Conversation Settings'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
    >
      <TwoColumnLayout>
        <Column>
          <SettingRow>
            <SettingInfo>
              <SettingTitle>
                <BellOutlined style={{ marginRight: 8 }} />
                {t('settings.mute') || 'Mute Notifications'}
              </SettingTitle>
              <SettingDescription>
                {t('settings.muteDescription') || 'Stop receiving notifications from this conversation'}
              </SettingDescription>
            </SettingInfo>
            <Switch checked={isMuted} onChange={handleMute} />
          </SettingRow>

          <Divider style={{ margin: 0 }} />

          <SettingRow>
            <SettingInfo>
              <SettingTitle>
                <PushpinOutlined style={{ marginRight: 8 }} />
                {t('settings.pin') || 'Pin Conversation'}
              </SettingTitle>
              <SettingDescription>
                {t('settings.pinDescription') || 'Keep this conversation at the top of your list'}
              </SettingDescription>
            </SettingInfo>
            <Switch checked={isPinned} onChange={handlePin} />
          </SettingRow>

          <Divider style={{ margin: 0 }} />

          <SettingRow>
            <SettingInfo>
              <SettingTitle>
                <InboxOutlined style={{ marginRight: 8 }} />
                {t('settings.archive') || 'Archive Conversation'}
              </SettingTitle>
              <SettingDescription>
                {t('settings.archiveDescription') || 'Hide this conversation from your main list'}
              </SettingDescription>
            </SettingInfo>
            <Switch checked={isArchived} onChange={handleArchive} />
          </SettingRow>

          {supportsNotifications && (
            <>
              <Divider style={{ margin: 0 }} />

              <SettingRow>
                <SettingInfo>
                  <SettingTitle>
                    <NotificationOutlined style={{ marginRight: 8 }} />
                    {t('notifications.browserNotifications') || 'Browser Notifications'}
                  </SettingTitle>
                  <SettingDescription>
                    {t('notifications.browserNotificationsDescription') || 'Receive browser notifications for new messages'}
                  </SettingDescription>
                </SettingInfo>
                <Switch
                  checked={notificationSettings.enabled}
                  onChange={(checked) => {
                    updateNotificationSettings({ enabled: checked });
                    if (checked && permission === 'default') {
                      setShowPermissionPrompt(true);
                    }
                  }}
                  disabled={permission === 'denied'}
                />
              </SettingRow>

              {showPermissionPrompt && permission === 'default' && (
                <Alert
                  message={t('notifications.permissionRequired') || 'Permission Required'}
                  description={
                    <div>
                      <div>{t('notifications.permissionDescription') || 'Please allow notifications to receive browser notifications for new messages.'}</div>
                      {typeof globalThis.navigator !== 'undefined' && globalThis.navigator.platform.toUpperCase().indexOf('MAC') >= 0 && (
                        <div style={{ marginTop: 8, fontWeight: 500, color: '#1890ff' }}>
                          {t('notifications.macOSPermissionNote') || 'Note: On macOS, you also need to enable notifications in System Settings → Notifications & Focus → [Your Browser].'}
                        </div>
                      )}
                    </div>
                  }
                  type="info"
                  action={
                    <Button size="small" onClick={handleRequestPermission}>
                      {t('notifications.allow') || 'Allow'}
                    </Button>
                  }
                  closable
                  onClose={() => setShowPermissionPrompt(false)}
                  style={{ marginTop: 8 }}
                />
              )}

              {permission === 'denied' && (
                <Alert
                  message={t('notifications.permissionDeniedTitle') || 'Notifications Blocked'}
                  description={t('notifications.permissionDeniedDescription') || 'Notifications are blocked. Please enable them in your browser settings.'}
                  type="warning"
                  style={{ marginTop: 8 }}
                />
              )}
            </>
          )}
        </Column>

        <Column>
          {supportsNotifications && notificationSettings.enabled && permission === 'granted' && (
            <>
              <SettingRow>
                <SettingInfo>
                  <SettingTitle>
                    <SoundOutlined style={{ marginRight: 8 }} />
                    {t('notifications.sound') || 'Sound Notifications'}
                  </SettingTitle>
                  <SettingDescription>
                    {t('notifications.soundDescription') || 'Play sound when receiving new messages'}
                  </SettingDescription>
                </SettingInfo>
                <Switch
                  checked={notificationSettings.soundEnabled}
                  onChange={(checked) => updateNotificationSettings({ soundEnabled: checked })}
                />
              </SettingRow>

              {notificationSettings.soundEnabled && (
                <>
                  <Divider style={{ margin: 0 }} />

                  <SettingRow>
                    <SettingInfo>
                      <SettingTitle>
                        {t('notifications.volume') || 'Sound Volume'}
                      </SettingTitle>
                      <SettingDescription>
                        {t('notifications.volumeDescription') || 'Adjust notification sound volume'}
                      </SettingDescription>
                    </SettingInfo>
                    <div style={{ width: 150 }}>
                      <Slider
                        min={0}
                        max={100}
                        value={notificationSettings.soundVolume * 100}
                        onChange={(value) => updateNotificationSettings({ soundVolume: value / 100 })}
                        tooltip={{ formatter: (value) => `${value}%` }}
                      />
                    </div>
                  </SettingRow>

                  <Divider style={{ margin: 0 }} />

                  <SettingRow>
                    <SettingInfo>
                      <SettingTitle>
                        {t('notifications.testSound') || 'Test Sound'}
                      </SettingTitle>
                      <SettingDescription>
                        {t('notifications.testSoundDescription') || 'Click to test the notification sound'}
                      </SettingDescription>
                    </SettingInfo>
                    <Button
                      size="small"
                      icon={<SoundOutlined />}
                      onClick={async () => {
                        try {
                          const { playNotificationSound, unlockAutoplay } = await import('@municipality/chat');
                          // Unlock autoplay first (this is a user interaction, so it will work)
                          await unlockAutoplay();
                          // Small delay to ensure instance is stored
                          await new Promise(resolve => setTimeout(resolve, 100));
                          // Then play the sound
                          await playNotificationSound(notificationSettings.soundVolume);
                          message.success(t('notifications.testSoundSuccess') || 'Sound played successfully');
                        } catch (error) {
                          console.error('Test sound error:', error);
                          const errorMessage = error instanceof Error ? error.message : String(error);
                          if (errorMessage.includes('load') || errorMessage.includes('timeout')) {
                            message.error(t('notifications.testSoundError') || 'Sound file failed to load. Please check if the file exists.');
                          } else {
                            message.error(t('notifications.testSoundError') || 'Failed to play test sound. Check console for details.');
                          }
                        }
                      }}
                    >
                      {t('notifications.test') || 'Test'}
                    </Button>
                  </SettingRow>
                </>
              )}

              <Divider style={{ margin: 0 }} />

              <SettingRow>
                <SettingInfo>
                  <SettingTitle>
                    {t('notifications.testNotification') || 'Test Notification'}
                  </SettingTitle>
                  <SettingDescription>
                    {t('notifications.testNotificationDescription') || 'Send a test browser notification to verify it works'}
                  </SettingDescription>
                </SettingInfo>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      if (globalThis.Notification.permission !== 'granted') {
                        message.error(t('notifications.permissionDenied') || 'Notification permission not granted');
                        return;
                      }

                      const testNotification = new globalThis.Notification('Test Notification', {
                        body: 'This is a test notification. If you see this, notifications are working!',
                        icon: '/favicon.ico',
                        tag: 'test-notification',
                        requireInteraction: false,
                      });

                      testNotification.onclick = () => {
                        window.focus();
                        testNotification.close();
                      };

                      testNotification.onerror = (error) => {
                        console.error('Test notification error:', error);
                        message.error(t('notifications.testNotificationError') || 'Notification failed. Check macOS notification settings.');
                      };

                      // Close after 5 seconds
                      setTimeout(() => {
                        testNotification.close();
                      }, 5000);

                      message.success(t('notifications.testNotificationSent') || 'Test notification sent! Check Notification Center if it doesn\'t appear.');
                    } catch (error) {
                      console.error('Test notification error:', error);
                      message.error(t('notifications.testNotificationError') || 'Failed to send test notification. Check console for details.');
                    }
                  }}
                >
                  {t('notifications.test') || 'Test'}
                </Button>
              </SettingRow>
            </>
          )}
        </Column>
      </TwoColumnLayout>
    </Modal>
  );
};

